/**
 * Jump To List Scroll Manager
 * Dynamically adds/removes CSS classes to show fade effects based on scroll position
 * Used for mobile horizontal scrolling of the brand jump-to navigation
 */

if (!customElements.get('jump-to-list')) {
  class JumpToList extends HTMLElement {
    constructor() {
      super();
      this.scrollContainer = this.querySelector('.jump-to-list');
      this.updateTimeout = null;
      this.resizeObserver = null;
    }

    connectedCallback() {
      // Wait for next frame to ensure element is fully rendered
      requestAnimationFrame(() => {
        this.init();
      });
    }

    init() {
      if (!this.scrollContainer) {
        console.warn('Jump-to-list: scroll container not found');
        return;
      }

      // Bind methods
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);

      // Initial state update
      this.updateFadeState();

      // Listen to scroll events with debouncing for performance
      this.scrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });

      // Listen to resize events
      window.addEventListener('resize', this.handleResize);

      // Use ResizeObserver to detect when container or children change size
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateFadeState();
        });
        this.resizeObserver.observe(this.scrollContainer);
      }

      // Listen for breakpoint changes (Enterprise theme pattern)
      window.addEventListener('on:breakpoint-change', this.handleResize);
    }

    disconnectedCallback() {
      // Clean up event listeners
      if (this.scrollContainer) {
        this.scrollContainer.removeEventListener('scroll', this.handleScroll);
      }
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('on:breakpoint-change', this.handleResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
    }

    handleScroll() {
      // Use requestAnimationFrame for smooth updates
      if (!this.updateTimeout) {
        this.updateTimeout = requestAnimationFrame(() => {
          this.updateFadeState();
          this.updateTimeout = null;
        });
      }
    }

    handleResize() {
      // Debounce resize events
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.updateFadeState();
      }, 150);
    }

    updateFadeState() {
      if (!this.scrollContainer) return;

      const scrollLeft = this.scrollContainer.scrollLeft;
      const scrollWidth = this.scrollContainer.scrollWidth;
      const clientWidth = this.scrollContainer.clientWidth;

      // Calculate max scroll position (account for rounding errors)
      const maxScroll = scrollWidth - clientWidth;

      // Threshold for detecting scroll position (1px tolerance for rounding)
      const threshold = 1;

      // Determine if we can scroll left or right
      const canScrollLeft = scrollLeft > threshold;
      const canScrollRight = scrollLeft < (maxScroll - threshold);

      // Update CSS classes
      this.scrollContainer.classList.toggle('can-scroll-left', canScrollLeft);
      this.scrollContainer.classList.toggle('can-scroll-right', canScrollRight);

      // Debug logging (remove in production if needed)
      if (this.dataset.debug === 'true') {
        console.log('Jump-to scroll state:', {
          scrollLeft,
          scrollWidth,
          clientWidth,
          maxScroll,
          canScrollLeft,
          canScrollRight
        });
      }
    }
  }

  customElements.define('jump-to-list', JumpToList);
}
